/**
 * Route Preloader
 * 
 * Preloads route chunks on hover/focus to improve perceived performance.
 * This makes navigation feel instant by loading code before the user clicks.
 */

type PreloadFunction = () => Promise<any>;

const preloadCache = new Set<string>();

/**
 * Preload a route chunk
 * @param routePath - Unique identifier for the route
 * @param preloadFn - Function that returns the lazy import
 */
export function preloadRoute(routePath: string, preloadFn: PreloadFunction): void {
  // Avoid preloading the same route multiple times
  if (preloadCache.has(routePath)) {
    return;
  }

  preloadCache.add(routePath);

  // Preload on next idle time to avoid blocking main thread
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => preloadFn());
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => preloadFn(), 1);
  }
}

/**
 * Preload multiple routes
 */
export function preloadRoutes(routes: Array<{ path: string; loader: PreloadFunction }>): void {
  routes.forEach(({ path, loader }) => preloadRoute(path, loader));
}

/**
 * Hook to add preloading to navigation links
 * 
 * Usage:
 * ```tsx
 * <Link 
 *   to="/explore" 
 *   onMouseEnter={() => preloadRoute('explore', () => import('./pages/Explore'))}
 * >
 *   Explore
 * </Link>
 * ```
 */
export function getPreloadHandlers(routePath: string, preloadFn: PreloadFunction) {
  return {
    onMouseEnter: () => preloadRoute(routePath, preloadFn),
    onFocus: () => preloadRoute(routePath, preloadFn),
  };
}

/**
 * Preload critical routes on app mount
 * Call this in your App component after initial render
 */
export function preloadCriticalRoutes(): void {
  // Preload common routes that users are likely to visit
  const criticalRoutes = [
    { path: 'artwork-detail', loader: () => import(/* webpackChunkName: "shopping" */ '../pages/ArtworkDetail') },
    { path: 'dashboard', loader: () => import(/* webpackChunkName: "user-account" */ '../pages/Dashboard') },
    { path: 'checkout', loader: () => import(/* webpackChunkName: "shopping" */ '../pages/Checkout') },
  ];

  // Delay preloading to avoid impacting initial page load
  setTimeout(() => {
    preloadRoutes(criticalRoutes);
  }, 2000);
}
